import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Loader2, ArrowLeft, MessageSquare, ArrowUp } from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import KarmaBadge, { tierFor } from "@/components/KarmaBadge";

const MEDAL = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/leaderboard?limit=50");
        setRows(data);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-8 py-6 flex-1 max-w-4xl mx-auto w-full">
          <Link to="/" className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6" data-testid="leaderboard-back-home">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <header className="mb-6">
            <h1 className="font-display text-3xl font-extrabold flex items-center gap-3">
              <Trophy className="w-8 h-8 text-[var(--tt-yellow)]" />
              Leaderboard
            </h1>
            <p className="text-sm text-[var(--tt-text-muted)] mt-2">Top contributors ranked by upvotes received.</p>
          </header>

          {loading ? (
            <div className="flex items-center gap-2 text-[var(--tt-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-[var(--tt-text-muted)] py-12 text-sm">
              No contributors yet. Be the first to start the discussion!
            </div>
          ) : (
            <ol className="space-y-2" data-testid="leaderboard-list">
              {rows.map((r) => (
                <li
                  key={r.user_id}
                  data-testid={`leaderboard-row-${r.user_id}`}
                  className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4 flex items-center gap-4 hover:border-[var(--tt-primary)]/40 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-display text-lg font-extrabold shrink-0"
                    style={r.rank <= 3 ? {
                      background: MEDAL[r.rank - 1],
                      color: "#000",
                    } : { background: "var(--tt-surface-2)", color: "var(--tt-text-muted)" }}
                  >
                    {r.rank}
                  </div>
                  {r.picture ? (
                    <img src={r.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-lg font-bold">
                      {r.name[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/u/${r.user_id}`}
                      className="font-display font-bold hover:text-[var(--tt-primary)] truncate block"
                      data-testid={`leaderboard-name-${r.user_id}`}
                    >
                      {r.name}
                    </Link>
                    <div className="flex items-center gap-2 mt-1">
                      <KarmaBadge karma={r.karma} />
                      <span className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt">
                        Joined {r.joined}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 font-display text-xl font-extrabold text-[var(--tt-primary)]">
                      <ArrowUp className="w-4 h-4" />
                      {r.karma}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-[var(--tt-text-muted)] mt-0.5 font-mono-tt justify-end">
                      <MessageSquare className="w-3 h-3" />
                      {r.comments}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
}
